import { notification } from 'antd';

import { KEY_AUTHENTICATED_USER } from '../authentication/AuthProvider';

const BASIC_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function authToken() {
  try {
    let auth = localStorage.getItem(KEY_AUTHENTICATED_USER) || sessionStorage.getItem(KEY_AUTHENTICATED_USER);
    if (auth) auth = JSON.parse(auth);
    return auth ? auth.webToken : '';
  } catch (e) {
    return '';
  }
}

/**
 * A 401 / 403 from the API means the token is missing, expired or revoked (the
 * app's own permission refusals come back as HTTP 200 with status: false), so
 * the stored session is dropped and the user is sent to the login page.
 */
function handleAuthFailure(response) {
  if ((response.status === 401 || response.status === 403) && !window.location.pathname.startsWith('/auth/')) {
    try {
      localStorage.removeItem(KEY_AUTHENTICATED_USER);
      sessionStorage.removeItem(KEY_AUTHENTICATED_USER);
    } catch (e) {
      /* storage unavailable */
    }
    window.location.assign('/auth/login');
  }
  return response;
}

let lastToastAt = 0;
function toastOnce(message, description) {
  const now = Date.now();
  if (now - lastToastAt < 4000) return; // one message for a burst of failing calls
  lastToastAt = now;
  notification.error({ message, description, duration: 6 });
}

/**
 * A network failure or a server error (HTTP 5xx) is reported once, in plain
 * words, and turned into a normal {status:false} answer so screens do not
 * crash on it.
 */
function handleServerFailure(response) {
  if (response.status >= 500) {
    return response
      .clone()
      .json()
      .catch(() => ({}))
      .then((body) => {
        toastOnce('The server could not complete the request', body.message || `HTTP ${response.status}`);
        return response;
      });
  }
  return response;
}
function networkFailure() {
  toastOnce('Cannot reach the server', 'Check your internet connection and try again.');
  const body = { status: false, message: 'Cannot reach the server.' };
  return { ok: false, status: 0, json: () => Promise.resolve(body), text: () => Promise.resolve(JSON.stringify(body)), blob: () => Promise.resolve(new Blob()), headers: new Headers() };
}

// The same POST/PUT/DELETE sent again while the first is still running (a
// double-clicked OK button) shares the first call's answer instead of creating
// the record twice.
const inFlight = new Map();

function jsonRequest(method) {
  return function (endPoint, data) {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    const key = isFormData ? null : `${method} ${endPoint} ${data !== undefined ? JSON.stringify(data) : ''}`;
    if (key && inFlight.has(key)) return inFlight.get(key).then((r) => (typeof r.clone === 'function' ? r.clone() : r));
    const call = fetch(BASIC_URL + endPoint, {
      method,
      headers: isFormData
        ? { 'Auth-WebToken': authToken() } // let the browser set Content-Type (with multipart boundary) itself
        : { 'Auth-WebToken': authToken(), 'Content-Type': 'application/json' },
      body: isFormData ? data : data !== undefined ? JSON.stringify(data) : undefined,
      mode: 'cors',
    })
      .then(handleAuthFailure)
      .then(handleServerFailure)
      .catch(networkFailure)
      .finally(() => key && inFlight.delete(key));
    if (key) inFlight.set(key, call);
    return call;
  };
}

const request = {
  get: function (endPoint, data) {
    const searchParams = new URLSearchParams(data);
    return fetch(BASIC_URL + endPoint + (data ? '?' + searchParams.toString() : ''), {
      method: 'GET',
      headers: { 'Auth-WebToken': authToken() },
      mode: 'cors',
    })
      .then(handleAuthFailure)
      .then(handleServerFailure)
      .catch(networkFailure);
  },
  post: jsonRequest('POST'),
  put: jsonRequest('PUT'),
  delete: jsonRequest('DELETE'),
  basicUrl: BASIC_URL,
};

export default request;
